import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BuildingOfficeIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { H1, H2, H3, Body, Card, Button, Badge, Spinner } from '../ui';
import { Input } from '../forms';
import { organizationsApi, OrganizationCreateData } from '../../services/api/organizations';
import { extractApiErrorMessage } from '../../utils/apiError';

interface OnboardingWizardProps {
  onComplete: () => void;
}

interface SchoolFormData {
  name: string;
  code: string;
  type: string;
  email: string;
  phone: string;
  address: string;
}

type WizardStep = 'school-details' | 'review' | 'provisioning' | 'complete';

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({ onComplete }) => {
  const [step, setStep] = useState<WizardStep>('school-details');
  const [formData, setFormData] = useState<SchoolFormData>({
    name: '',
    code: '',
    type: 'school',
    email: '',
    phone: '',
    address: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof SchoolFormData, string>>>({});
  const [provisioningStatus, setProvisioningStatus] = useState<'idle' | 'creating-org' | 'provisioning' | 'done' | 'error'>('idle');
  const [provisioningError, setProvisioningError] = useState('');
  const [createdOrgId, setCreatedOrgId] = useState<string>('');

  const updateField = (field: keyof SchoolFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  // Auto-generate code from name
  const handleNameChange = (value: string) => {
    updateField('name', value);
    if (!formData.code || formData.code === generateCode(formData.name)) {
      updateField('code', generateCode(value));
    }
  };

  const generateCode = (name: string) => {
    return name
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '')
      .slice(0, 20);
  };

  const validateStep1 = (): boolean => {
    const newErrors: Partial<Record<keyof SchoolFormData, string>> = {};

    if (!formData.name.trim()) newErrors.name = 'School name is required';
    if (!formData.code.trim()) newErrors.code = 'Organization code is required';
    if (formData.code && !/^[A-Z0-9_]+$/.test(formData.code)) {
      newErrors.code = 'Code must contain only uppercase letters, numbers, and underscores';
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (step === 'school-details' && validateStep1()) {
      setStep('review');
    }
  };

  const handleBack = () => {
    if (step === 'review') setStep('school-details');
  };

  const handleProvision = async () => {
    setStep('provisioning');
    setProvisioningStatus('creating-org');
    setProvisioningError('');

    try {
      // Step 1: Create the organization
      const orgData: OrganizationCreateData = {
        name: formData.name,
        code: formData.code,
        type: formData.type,
        email: formData.email || undefined,
        phone: formData.phone || undefined,
        address: formData.address || undefined,
        is_active: true,
      };

      const org = await organizationsApi.createOrganization(orgData);
      setCreatedOrgId(org.id);

      // Step 2: Provision the tenant schema
      setProvisioningStatus('provisioning');
      await organizationsApi.provisionSchema(org.id);

      setProvisioningStatus('done');
      setStep('complete');
    } catch (err: unknown) {
      setProvisioningStatus('error');
      setProvisioningError(extractApiErrorMessage(err, 'An unexpected error occurred'));
    }
  };

  const stepIndicator = (
    <div className="flex items-center justify-center mb-8">
      {['School Details', 'Review', 'Setup'].map((label, index) => {
        const steps: WizardStep[] = ['school-details', 'review', 'provisioning'];
        const currentIndex = steps.indexOf(step === 'complete' ? 'provisioning' : step);
        const isActive = index <= currentIndex;
        const isComplete = index < currentIndex || step === 'complete';

        return (
          <React.Fragment key={label}>
            {index > 0 && (
              <div className={`h-0.5 w-12 mx-2 ${isActive ? 'bg-primary-500' : 'bg-gray-200'}`} />
            )}
            <div className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  isComplete
                    ? 'bg-primary-500 text-white'
                    : isActive
                    ? 'bg-primary-100 text-primary-700 border-2 border-primary-500'
                    : 'bg-gray-100 text-gray-400'
                }`}
              >
                {isComplete ? '✓' : index + 1}
              </div>
              <span className={`ml-2 text-sm hidden sm:inline ${isActive ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
                {label}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-6">
        <BuildingOfficeIcon className="w-12 h-12 text-primary-600 mx-auto mb-3" />
        <H1 className="mb-2">School Onboarding</H1>
        <Body className="text-gray-600">
          Set up a new school on the Sprout platform.
        </Body>
      </div>

      {stepIndicator}

      <AnimatePresence mode="wait">
        {/* Step 1: School Details */}
        {step === 'school-details' && (
          <motion.div
            key="school-details"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <Card className="p-6">
              <H2 className="mb-6">School Information</H2>

              <div className="space-y-4">
                <Input
                  label="School Name *"
                  value={formData.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="e.g. Greenwood Primary School"
                  error={errors.name}
                />

                <Input
                  label="Organization Code *"
                  value={formData.code}
                  onChange={(e) => updateField('code', e.target.value.toUpperCase())}
                  placeholder="e.g. GREENWOOD_PRIMARY"
                  error={errors.code}
                  helperText="Unique identifier for the school. Auto-generated from name."
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    School Type
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => updateField('type', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="school">School</option>
                    <option value="district">District</option>
                    <option value="demo">Demo</option>
                  </select>
                </div>

                <Input
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  placeholder="admin@school.co.za"
                  error={errors.email}
                />

                <Input
                  label="Phone"
                  value={formData.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                  placeholder="+27 11 123 4567"
                />

                <Input
                  label="Address"
                  value={formData.address}
                  onChange={(e) => updateField('address', e.target.value)}
                  placeholder="123 Main Street, Johannesburg"
                />
              </div>

              <div className="flex justify-end mt-6">
                <Button variant="primary" onClick={handleNext}>
                  Next: Review
                  <ArrowRightIcon className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Step 2: Review */}
        {step === 'review' && (
          <motion.div
            key="review"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <Card className="p-6">
              <H2 className="mb-6">Review & Confirm</H2>

              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <H3 className="mb-3">School Details</H3>
                  <dl className="space-y-2">
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-500">Name</dt>
                      <dd className="text-sm font-medium">{formData.name}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-500">Code</dt>
                      <dd className="text-sm font-medium font-mono">{formData.code}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-500">Type</dt>
                      <dd className="text-sm font-medium capitalize">{formData.type}</dd>
                    </div>
                    {formData.email && (
                      <div className="flex justify-between">
                        <dt className="text-sm text-gray-500">Email</dt>
                        <dd className="text-sm font-medium">{formData.email}</dd>
                      </div>
                    )}
                    {formData.phone && (
                      <div className="flex justify-between">
                        <dt className="text-sm text-gray-500">Phone</dt>
                        <dd className="text-sm font-medium">{formData.phone}</dd>
                      </div>
                    )}
                    {formData.address && (
                      <div className="flex justify-between">
                        <dt className="text-sm text-gray-500">Address</dt>
                        <dd className="text-sm font-medium">{formData.address}</dd>
                      </div>
                    )}
                  </dl>
                </div>

                <div className="bg-blue-50 rounded-lg p-4">
                  <H3 className="text-blue-900 mb-2">What happens next</H3>
                  <ul className="space-y-1 text-sm text-blue-800">
                    <li>• A new organization record will be created</li>
                    <li>• A dedicated database schema will be provisioned</li>
                    <li>• Default academic structure will be initialized</li>
                    <li>• Assessment scales will be set up</li>
                  </ul>
                </div>
              </div>

              <div className="flex justify-between mt-6">
                <Button variant="ghost" onClick={handleBack}>
                  <ArrowLeftIcon className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button variant="primary" onClick={handleProvision}>
                  Create School
                  <ArrowRightIcon className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Step 3: Provisioning */}
        {step === 'provisioning' && (
          <motion.div
            key="provisioning"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <Card className="p-6">
              <div className="text-center">
                {provisioningStatus === 'error' ? (
                  <>
                    <ExclamationTriangleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    <H2 className="mb-2 text-red-900">Setup Failed</H2>
                    <Body className="text-red-700 mb-4">{provisioningError}</Body>
                    <div className="flex justify-center space-x-3">
                      <Button variant="ghost" onClick={() => setStep('review')}>
                        Back to Review
                      </Button>
                      <Button variant="primary" onClick={handleProvision}>
                        Retry
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <Spinner size="lg" />
                    <H2 className="mt-4 mb-2">Setting up {formData.name}</H2>
                    <div className="space-y-3 mt-6">
                      <ProvisionStep
                        label="Creating organization"
                        status={
                          provisioningStatus === 'creating-org'
                            ? 'active'
                            : provisioningStatus === 'provisioning' || provisioningStatus === 'done'
                            ? 'done'
                            : 'pending'
                        }
                      />
                      <ProvisionStep
                        label="Provisioning database"
                        status={
                          provisioningStatus === 'provisioning'
                            ? 'active'
                            : provisioningStatus === 'done'
                            ? 'done'
                            : 'pending'
                        }
                      />
                      <ProvisionStep
                        label="Initializing data"
                        status={provisioningStatus === 'done' ? 'done' : 'pending'}
                      />
                    </div>
                  </>
                )}
              </div>
            </Card>
          </motion.div>
        )}

        {/* Step 4: Complete */}
        {step === 'complete' && (
          <motion.div
            key="complete"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <Card className="p-6">
              <div className="text-center">
                <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <H2 className="mb-2">School Created Successfully!</H2>
                <Body className="text-gray-600 mb-6">
                  <strong>{formData.name}</strong> has been set up and is ready to use.
                </Body>

                <div className="bg-green-50 rounded-lg p-4 mb-6 text-left">
                  <H3 className="text-green-900 mb-2">Next Steps</H3>
                  <ul className="space-y-1 text-sm text-green-800">
                    <li>• Create teacher and staff user accounts</li>
                    <li>• Set up classrooms and grades</li>
                    <li>• Add children to classrooms</li>
                    <li>• Configure CAPS curriculum and learning areas</li>
                  </ul>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                  <H3 className="mb-2">Organization Details</H3>
                  <dl className="space-y-1">
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-500">Code</dt>
                      <dd className="text-sm font-mono font-medium">{formData.code}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-500">Schema</dt>
                      <dd className="text-sm font-mono font-medium">
                        {formData.code.toLowerCase()}
                      </dd>
                    </div>
                  </dl>
                </div>

                <Button variant="primary" onClick={onComplete}>
                  Done
                </Button>
              </div>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Sub-component for provisioning progress steps
const ProvisionStep: React.FC<{
  label: string;
  status: 'pending' | 'active' | 'done';
}> = ({ label, status }) => (
  <div className="flex items-center space-x-3">
    {status === 'done' && <CheckCircleIcon className="w-5 h-5 text-green-500" />}
    {status === 'active' && <Spinner size="sm" />}
    {status === 'pending' && <div className="w-5 h-5 rounded-full border-2 border-gray-200" />}
    <Body className={status === 'pending' ? 'text-gray-400' : 'text-gray-900'}>
      {label}
    </Body>
    {status === 'done' && <Badge variant="success" size="sm">Done</Badge>}
  </div>
);

export default OnboardingWizard;
